import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import Paper from '@mui/material/Paper';
import { useCallback, useState } from 'react';

import Loader from 'components/Loader';
import { useGetDomainAuthQuery, useGetMemberNotificationsQuery } from 'store/api/version-api.slice';

import { DEFAULT_EMAIL_FORM_VALUES } from '../constants';
import { getEmailIconByType } from '../helpers';
import { styles } from '../styles/emailsList.styles';

import EditNotificationEmailDialog from './EditNotificationEmailDialog';
import EmailListItem from './EmailListItem';
import VerifyDomainDialog from './VerifyDomainDialog';

const EmailsList = () => {
	const { data: memberNotificationsResult = {}, isLoading } = useGetMemberNotificationsQuery();
	const { data: domainAuthResponse, isFetching } = useGetDomainAuthQuery();

	const isDKIMSetupFailed = !isFetching && domainAuthResponse?.result.sg_domain_id === 0;

	const memberNotifications = memberNotificationsResult.results || [];

	const [editEmailFormValues, setEditEmailFormValues] = useState(DEFAULT_EMAIL_FORM_VALUES);
	const [editDialogOpen, setEditDialogOpen] = useState(false);
	const [toggleTestEmail, setToggleTestEmail] = useState(false);
	const [verifyDomainOpen, setVerifyDomainOpen] = useState(false);

	const closeVerifyDomainDialog = () => {
		setVerifyDomainOpen(false);
	};

	const onEditEmail = id => () => {
		const emailToEdit = memberNotifications.find(item => item.id === id);

		if (emailToEdit) {
			setEditEmailFormValues(emailToEdit);
			setEditDialogOpen(true);
		}
	};

	const handleCloseEditDialog = () => setEditDialogOpen(false);

	const handleEditFormValueChange = useCallback((prop, value) => {
		setEditEmailFormValues(previousState => ({
			...previousState,
			[prop]: value
		}));
	}, []);

	const handleToggleTestEmail = () => {
		setToggleTestEmail(prev => !prev);
	};

	const handleDKIMSetup = () => {
		setVerifyDomainOpen(true);
	};

	return (
		<Grid item sm={5} xs={12} sx={styles.emailsGrid}>
			<Paper sx={styles.emailsPaper}>
				{isDKIMSetupFailed && (
					<Alert
						severity="warning"
						onClick={handleDKIMSetup}
						sx={{
							fontSize: '10px',
							cursor: 'pointer',
							'&:hover': {
								backgroundColor: '#f7dfbe'
							}
						}}
					>
						DKIM (DomainKeys Identified Mail) is not setup. We cannot guarantee email delivery until
						complete. Click to setup.
					</Alert>
				)}
				{isLoading && <Loader variant="ghost" />}
				<List sx={styles.emailsList}>
					{memberNotifications.map(item => {
						const IconComponent = getEmailIconByType(item.email_type);

						return (
							<EmailListItem
								key={item.id}
								className={styles.listItem}
								item={item}
								IconComponent={IconComponent}
								onEditEmail={onEditEmail}
							/>
						);
					})}
				</List>
			</Paper>
			<VerifyDomainDialog isOpen={verifyDomainOpen} onClose={closeVerifyDomainDialog} />
			<EditNotificationEmailDialog
				editDialogOpen={editDialogOpen}
				editEmailFormValues={editEmailFormValues}
				handleCloseEditDialog={handleCloseEditDialog}
				handleEditFormValueChange={handleEditFormValueChange}
				handleToggleTestEmail={handleToggleTestEmail}
				toggleTestEmail={toggleTestEmail}
			/>
		</Grid>
	);
};

export default EmailsList;
